import { useEffect, useRef, useState } from "react";
import { ChevronDown, ChevronUp, ImagePlus, Pencil, Plus, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { API, api, apiError, fmt } from "@/lib/api";

const EMPTY = { id: null, title: "", category: "", price: "", desc: "", photo_path: null, subcategory: "", active: true };

const slugify = (s) =>
  s.toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

export default function MenuPanel() {
  const [items, setItems] = useState([]);
  const [cats, setCats] = useState([]);
  const [form, setForm] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [newCatLabel, setNewCatLabel] = useState("");
  const [newSubLabel, setNewSubLabel] = useState({});
  const [editingCat, setEditingCat] = useState(null);
  const [showCats, setShowCats] = useState(false);
  const fileRef = useRef(null);

  const load = async () => {
    try {
      const [m, c] = await Promise.all([api.get("/admin/menu"), api.get("/categories")]);
      setItems(m.data);
      setCats(c.data);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  useEffect(() => {
    load();
  }, []);

  const sortedCats = [...cats].sort((a, b) => a.sort - b.sort);

  // ---------- category & subcategory ops ----------
  const patchCat = async (id, payload) => {
    await api.patch(`/admin/categories/${id}`, payload).catch((e) => toast.error(apiError(e)));
    load();
  };

  const addCat = async () => {
    if (!newCatLabel.trim()) return;
    try {
      await api.post("/admin/categories", { label: newCatLabel.trim() });
      toast.success("Category added");
      setNewCatLabel("");
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const moveCat = async (cat, dir) => {
    const idx = sortedCats.findIndex((c) => c.id === cat.id);
    const nb = sortedCats[idx + dir];
    if (!nb) return;
    await api.patch(`/admin/categories/${cat.id}`, { sort: nb.sort }).catch(() => {});
    await api.patch(`/admin/categories/${nb.id}`, { sort: cat.sort }).catch(() => {});
    load();
  };

  const deleteCat = async (cat) => {
    if (!window.confirm(`Delete category "${cat.label}"?`)) return;
    await api.delete(`/admin/categories/${cat.id}`).catch((e) => toast.error(apiError(e)));
    load();
  };

  const saveCatLabel = async () => {
    if (!editingCat?.label.trim()) return;
    await patchCat(editingCat.id, { label: editingCat.label.trim() });
    setEditingCat(null);
    toast.success("Category renamed");
  };

  const addSub = (cat) => {
    const label = (newSubLabel[cat.id] || "").trim();
    if (!label) return;
    const subs = [...(cat.subs || []), { id: slugify(label), label, sort: (cat.subs || []).length }];
    setNewSubLabel({ ...newSubLabel, [cat.id]: "" });
    patchCat(cat.id, { subs });
    toast.success(`Subcategory "${label}" added`);
  };

  const moveSub = (cat, sub, dir) => {
    const subs = [...(cat.subs || [])].sort((a, b) => a.sort - b.sort);
    const idx = subs.findIndex((s) => s.id === sub.id);
    const nb = subs[idx + dir];
    if (!nb) return;
    [subs[idx], subs[idx + dir]] = [nb, sub];
    updateSubsSorted(cat, subs);
  };

  const deleteSub = (cat, sub) => {
    if (!window.confirm(`Delete subcategory "${sub.label}"? Items inside become ungrouped.`)) return;
    updateSubsSorted(cat, (cat.subs || []).filter((s) => s.id !== sub.id));
  };

  const updateSubsSorted = (cat, subs) =>
    patchCat(cat.id, { subs: subs.map((s, i) => ({ ...s, sort: i })) });

  // ---------- item ops ----------
  const groups = [];
  for (const cat of sortedCats) {
    const catItems = items.filter((i) => i.category === cat.id).sort((a, b) => a.sort - b.sort);
    if (cat.subs?.length) {
      for (const sub of [...cat.subs].sort((a, b) => a.sort - b.sort)) {
        groups.push({
          key: `${cat.id}:${sub.id}`,
          label: `${cat.label} · ${sub.label}`,
          cat,
          subId: sub.id,
          items: catItems.filter((i) => i.subcategory === sub.id),
        });
      }
      const rest = catItems.filter((i) => !cat.subs.some((s) => s.id === i.subcategory));
      if (rest.length) {
        groups.push({ key: `${cat.id}:other`, label: `${cat.label} · Ungrouped`, cat, subId: "", items: rest });
      }
    } else {
      groups.push({ key: cat.id, label: cat.label, cat, subId: "", items: catItems });
    }
  }
  const orphanItems = items.filter((i) => !cats.some((c) => c.id === i.category));
  if (orphanItems.length) {
    groups.push({ key: "orphans", label: "Uncategorized", cat: null, subId: "", items: orphanItems });
  }

  const moveItem = async (group, item, dir) => {
    const list = group.items;
    const idx = list.findIndex((i) => i.id === item.id);
    const nb = list[idx + dir];
    if (!nb) return;
    const itemNewSort = nb.sort === item.sort ? nb.sort + dir : nb.sort;
    await api.patch(`/admin/menu/${item.id}`, { sort: itemNewSort }).catch(() => {});
    await api.patch(`/admin/menu/${nb.id}`, { sort: item.sort }).catch(() => {});
    load();
  };

  const openEdit = (it) =>
    setForm({
      id: it.id,
      title: it.title,
      category: it.category,
      price: (it.price_cents / 100).toFixed(2),
      desc: it.desc || "",
      photo_path: it.photo_path || null,
      subcategory: it.subcategory || "",
      active: it.active,
    });

  const save = async () => {
    const price_cents = Math.round(parseFloat(form.price) * 100);
    if (!form.title.trim() || !price_cents || !form.category) {
      toast.error("Title, price and category are required");
      return;
    }
    const payload = {
      title: form.title.trim(),
      category: form.category,
      subcategory: form.subcategory || "",
      desc: form.desc,
      price_cents,
      photo_path: form.photo_path,
      active: form.active,
    };
    try {
      if (form.id) {
        await api.patch(`/admin/menu/${form.id}`, payload);
        toast.success("Item updated");
      } else {
        await api.post("/admin/menu", payload);
        toast.success("Item added to the menu");
      }
      setForm(null);
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this item from the menu?")) return;
    await api.delete(`/admin/menu/${id}`).catch((e) => toast.error(apiError(e)));
    load();
  };

  const toggleActive = async (it) => {
    await api.patch(`/admin/menu/${it.id}`, { active: !it.active }).catch(() => {});
    load();
  };

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setForm((f) => ({ ...f, photo_path: data.path }));
      toast.success("Photo uploaded");
    } catch (err) {
      toast.error(apiError(err));
    }
    setUploading(false);
  };

  const formCat = cats.find((c) => c.id === form?.category);

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-chalk/50">{items.length} items · changes appear on the site instantly</p>
        <div className="flex gap-2">
          <button
            data-testid="cats-manage-toggle"
            onClick={() => setShowCats((s) => !s)}
            className="rounded-full border-2 border-grape px-5 py-2.5 text-sm font-bold text-grape transition-colors hover:bg-grape hover:text-cream"
          >
            {showCats ? "Hide Categories" : "Manage Categories"}
          </button>
          <button
            data-testid="menu-add-button"
            onClick={() => setForm({ ...EMPTY, category: sortedCats[0]?.id || "" })}
            className="flex items-center gap-1.5 rounded-full bg-artichoke px-5 py-2.5 text-sm font-bold text-cream transition-colors hover:bg-deep"
          >
            <Plus className="h-4 w-4" /> Add Item
          </button>
        </div>
      </div>

      {showCats && (
        <div data-testid="cats-manager" className="mt-5 rounded-3xl border-2 border-grape/30 bg-cream p-5">
          <h3 className="font-display text-lg font-semibold text-deep">Categories & subcategories</h3>
          <div className="mt-4 space-y-3">
            {sortedCats.map((cat, ci) => (
              <div key={cat.id} data-testid={`cat-row-${cat.id}`} className="rounded-2xl border border-leafline bg-sage/50 p-4">
                <div className="flex flex-wrap items-center gap-2">
                  {editingCat?.id === cat.id ? (
                    <>
                      <input
                        data-testid={`cat-rename-input-${cat.id}`}
                        value={editingCat.label}
                        onChange={(e) => setEditingCat({ ...editingCat, label: e.target.value })}
                        className="rounded-xl border-2 border-grape bg-white px-3 py-1.5 text-sm font-medium outline-none"
                      />
                      <button data-testid={`cat-rename-save-${cat.id}`} onClick={saveCatLabel} className="rounded-full bg-grape px-4 py-1.5 text-xs font-bold text-cream">
                        Save
                      </button>
                      <button onClick={() => setEditingCat(null)} className="text-xs font-bold text-chalk/50">Cancel</button>
                    </>
                  ) : (
                    <>
                      <span className="font-display text-base font-semibold text-deep">{cat.label}</span>
                      <span className="text-xs text-chalk/40">{items.filter((i) => i.category === cat.id).length} items</span>
                    </>
                  )}
                  <span className="flex-1" />
                  <button data-testid={`cat-up-${cat.id}`} onClick={() => moveCat(cat, -1)} disabled={ci === 0} className="rounded-full border border-chalk/10 p-1.5 text-chalk/60 hover:border-grape hover:text-grape disabled:opacity-25" aria-label="Move category up">
                    <ChevronUp className="h-4 w-4" />
                  </button>
                  <button data-testid={`cat-down-${cat.id}`} onClick={() => moveCat(cat, 1)} disabled={ci === sortedCats.length - 1} className="rounded-full border border-chalk/10 p-1.5 text-chalk/60 hover:border-grape hover:text-grape disabled:opacity-25" aria-label="Move category down">
                    <ChevronDown className="h-4 w-4" />
                  </button>
                  <button data-testid={`cat-rename-${cat.id}`} onClick={() => setEditingCat({ id: cat.id, label: cat.label })} className="rounded-full border border-chalk/10 p-1.5 text-chalk/60 hover:border-grape hover:text-grape" aria-label="Rename category">
                    <Pencil className="h-4 w-4" />
                  </button>
                  <button data-testid={`cat-delete-${cat.id}`} onClick={() => deleteCat(cat)} className="rounded-full border border-chalk/10 p-1.5 text-chalk/60 hover:border-red-300 hover:text-red-600" aria-label="Delete category">
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  {(cat.subs || []).map((s, si) => (
                    <span key={s.id} data-testid={`sub-row-${s.id}`} className="flex items-center gap-1 rounded-full bg-white px-3 py-1.5 text-xs font-bold text-chalk/70 shadow-sm">
                      {s.label}
                      <button onClick={() => moveSub(cat, s, -1)} disabled={si === 0} className="text-chalk/40 hover:text-grape disabled:opacity-20" aria-label="Move sub up">
                        <ChevronUp className="h-3 w-3" />
                      </button>
                      <button onClick={() => moveSub(cat, s, 1)} disabled={si === cat.subs.length - 1} className="text-chalk/40 hover:text-grape disabled:opacity-20" aria-label="Move sub down">
                        <ChevronDown className="h-3 w-3" />
                      </button>
                      <button onClick={() => deleteSub(cat, s)} className="text-chalk/40 hover:text-red-500" aria-label="Delete sub">
                        <X className="h-3 w-3" />
                      </button>
                    </span>
                  ))}
                  <span className="flex items-center gap-1.5">
                    <input
                      data-testid={`sub-add-input-${cat.id}`}
                      value={newSubLabel[cat.id] || ""}
                      onChange={(e) => setNewSubLabel({ ...newSubLabel, [cat.id]: e.target.value })}
                      placeholder="New subcategory"
                      className="w-36 rounded-full border-2 border-chalk/10 bg-white px-3 py-1.5 text-xs font-medium outline-none focus:border-grape"
                    />
                    <button data-testid={`sub-add-button-${cat.id}`} onClick={() => addSub(cat)} className="rounded-full bg-grape/15 px-3 py-1.5 text-xs font-bold text-grape hover:bg-grape hover:text-cream">
                      + Add
                    </button>
                  </span>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-4 flex gap-2 border-t border-dashed border-chalk/10 pt-4">
            <input
              data-testid="cat-add-input"
              value={newCatLabel}
              onChange={(e) => setNewCatLabel(e.target.value)}
              placeholder="New category name (e.g. Desserts)"
              className="w-64 rounded-2xl border-2 border-chalk/10 bg-white px-4 py-2.5 text-sm font-medium outline-none focus:border-grape"
            />
            <button data-testid="cat-add-button" onClick={addCat} className="rounded-full bg-grape px-5 py-2.5 text-sm font-bold text-cream transition-colors hover:brightness-110">
              + Add Category
            </button>
          </div>
        </div>
      )}

      {form && (
        <div data-testid="menu-item-form" className="mt-5 rounded-3xl border-2 border-artichoke/30 bg-cream p-6 shadow-lg">
          <div className="flex items-center justify-between">
            <h3 className="font-display text-lg font-semibold text-deep">
              {form.id ? "Edit item" : "New item"}
            </h3>
            <button onClick={() => setForm(null)} className="rounded-full p-1.5 hover:bg-sage" aria-label="Close form">
              <X className="h-5 w-5" />
            </button>
          </div>
          <div className="mt-4 grid gap-3 sm:grid-cols-2">
            <input
              data-testid="menu-form-title"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              placeholder="Item name *"
              className="rounded-2xl border-2 border-chalk/10 bg-white px-4 py-2.5 text-sm font-medium outline-none focus:border-artichoke"
            />
            <div className="flex gap-3">
              <select
                data-testid="menu-form-category"
                value={form.category}
                onChange={(e) => setForm({ ...form, category: e.target.value, subcategory: "" })}
                className="flex-1 rounded-2xl border-2 border-chalk/10 bg-white px-4 py-2.5 text-sm font-medium outline-none focus:border-artichoke"
              >
                <option value="">Category…</option>
                {sortedCats.map((c) => (
                  <option key={c.id} value={c.id}>{c.label}</option>
                ))}
              </select>
              <input
                data-testid="menu-form-price"
                value={form.price}
                onChange={(e) => setForm({ ...form, price: e.target.value })}
                placeholder="Price * (2.75)"
                inputMode="decimal"
                className="w-32 rounded-2xl border-2 border-chalk/10 bg-white px-4 py-2.5 text-sm font-medium outline-none focus:border-artichoke"
              />
            </div>
            {formCat?.subs?.length > 0 && (
              <select
                data-testid="menu-form-subcategory"
                value={form.subcategory}
                onChange={(e) => setForm({ ...form, subcategory: e.target.value })}
                className="rounded-2xl border-2 border-chalk/10 bg-white px-4 py-2.5 text-sm font-medium outline-none focus:border-artichoke sm:col-span-2"
              >
                <option value="">Subcategory…</option>
                {[...formCat.subs].sort((a, b) => a.sort - b.sort).map((s) => (
                  <option key={s.id} value={s.id}>{s.label}</option>
                ))}
              </select>
            )}
            <textarea
              data-testid="menu-form-desc"
              value={form.desc}
              onChange={(e) => setForm({ ...form, desc: e.target.value })}
              placeholder="Short description"
              rows={2}
              className="rounded-2xl border-2 border-chalk/10 bg-white px-4 py-2.5 text-sm font-medium outline-none focus:border-artichoke sm:col-span-2"
            />
            <div className="flex items-center gap-4 sm:col-span-2">
              {form.photo_path ? (
                <img src={`${API}/files/${form.photo_path}`} alt="Item" className="h-20 w-20 rounded-2xl border border-leafline object-cover" data-testid="menu-form-photo-preview" />
              ) : (
                <div className="flex h-20 w-20 items-center justify-center rounded-2xl border-2 border-dashed border-chalk/15 text-chalk/30">
                  <ImagePlus className="h-6 w-6" />
                </div>
              )}
              <div>
                <button
                  data-testid="menu-form-upload-button"
                  onClick={() => fileRef.current?.click()}
                  disabled={uploading}
                  className="rounded-full border-2 border-artichoke px-5 py-2 text-sm font-bold text-artichoke transition-colors hover:bg-artichoke hover:text-cream disabled:opacity-60"
                >
                  {uploading ? "Uploading…" : form.photo_path ? "Change photo" : "Add photo from camera roll"}
                </button>
                <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={upload} data-testid="menu-form-file-input" />
                <p className="mt-1.5 text-[11px] text-chalk/45">JPG, PNG or HEIC from your phone, up to 10 MB.</p>
              </div>
            </div>
          </div>
          <div className="mt-5 flex gap-3">
            <button
              data-testid="menu-form-save-button"
              onClick={save}
              className="rounded-full bg-artichoke px-7 py-2.5 font-display text-base font-semibold text-cream transition-colors hover:bg-deep"
            >
              Save Item
            </button>
            <button onClick={() => setForm(null)} className="rounded-full border-2 border-chalk/10 px-6 py-2.5 text-sm font-bold text-chalk/60">
              Cancel
            </button>
          </div>
        </div>
      )}

      <div className="mt-6 space-y-6">
        {groups.map((g) => (
          <div key={g.key} data-testid={`menu-group-${g.key}`}>
            <div className="flex items-center justify-between border-b-2 border-chalk/10 pb-2">
              <h3 className="font-display text-base font-bold text-artichoke">
                {g.label}
                <span className="ml-2 text-xs font-semibold text-chalk/40">{g.items.length}</span>
              </h3>
              {g.cat && (
                <button
                  data-testid={`menu-add-${g.key}`}
                  onClick={() => setForm({ ...EMPTY, category: g.cat.id, subcategory: g.subId })}
                  className="flex items-center gap-1 rounded-full bg-artichoke/10 px-3.5 py-1.5 text-xs font-bold text-artichoke transition-colors hover:bg-artichoke hover:text-cream"
                >
                  <Plus className="h-3.5 w-3.5" /> Add here
                </button>
              )}
            </div>
            {g.items.length === 0 ? (
              <p className="py-3 text-xs italic text-chalk/40">No items yet</p>
            ) : (
              <div className="mt-2 space-y-2">
                {g.items.map((it, ii) => (
                  <div
                    key={it.id}
                    data-testid={`menu-row-${it.id}`}
                    className={`flex items-center gap-3 rounded-2xl border border-leafline bg-cream p-3.5 ${!it.active ? "opacity-50" : ""}`}
                  >
                    <span className="flex flex-col">
                      <button data-testid={`menu-up-${it.id}`} onClick={() => moveItem(g, it, -1)} disabled={ii === 0} className="text-chalk/40 hover:text-artichoke disabled:opacity-20" aria-label="Move item up">
                        <ChevronUp className="h-4 w-4" />
                      </button>
                      <button data-testid={`menu-down-${it.id}`} onClick={() => moveItem(g, it, 1)} disabled={ii === g.items.length - 1} className="text-chalk/40 hover:text-artichoke disabled:opacity-20" aria-label="Move item down">
                        <ChevronDown className="h-4 w-4" />
                      </button>
                    </span>
                    {it.photo_path ? (
                      <img src={`${API}/files/${it.photo_path}`} alt="" className="h-11 w-11 rounded-xl object-cover" />
                    ) : (
                      <div className="h-11 w-11 rounded-xl bg-sage" />
                    )}
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-bold">{it.title}</p>
                      <p className="text-xs text-chalk/45">{fmt(it.price_cents)}</p>
                    </div>
                    <button
                      data-testid={`menu-toggle-${it.id}`}
                      onClick={() => toggleActive(it)}
                      className={`rounded-full px-3.5 py-1.5 text-[11px] font-bold uppercase tracking-wide ${
                        it.active ? "bg-artichoke/15 text-artichoke" : "bg-chalk/10 text-chalk/50"
                      }`}
                    >
                      {it.active ? "On menu" : "Hidden"}
                    </button>
                    <button data-testid={`menu-edit-${it.id}`} onClick={() => openEdit(it)} className="rounded-full border border-chalk/10 p-2 text-chalk/60 transition-colors hover:border-artichoke hover:text-artichoke" aria-label="Edit">
                      <Pencil className="h-4 w-4" />
                    </button>
                    <button data-testid={`menu-delete-${it.id}`} onClick={() => remove(it.id)} className="rounded-full border border-chalk/10 p-2 text-chalk/60 transition-colors hover:border-red-300 hover:text-red-600" aria-label="Delete">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
